import _ from 'lodash'
import queryMapping from '@wix/document-services-json-schemas/dist/queryMapping.json'
import { createMaterializer } from '@wix/materializer'
import type { Update } from '@wix/thunderbolt-types'
import type { BaseDataItem, Component, Data, DataMapName, RefSchemas } from '@wix/thunderbolt-becky-types'
import { parseSvgString } from '@wix/thunderbolt-commons'
import { createCompRef, MegaStore } from '..'
import { dataNodes as baseDataNodes } from '../dataNodes/dataNodes'
import { compQueriesRefs } from '../refs/compQueriesRefs'
import { COMPS_ROOT_NAMESPACE, SVG_STORE_NAMESPACE } from '../refs/constants'
import { createRefResolver } from '../refs/schemaRefResolver'
import { DataStore } from '../stores/DataStore'
import type {
	Batched,
	Catharsis,
	CatharsisArgs,
	UpdateData,
	UpdateEnvironment,
	UpdatePathToSelectorMap,
	UpdateBuilderTypeToReducedManifest,
	UpdateBuilderTypeToPresetInfo,
	UpdateStructure,
	UpdateSvgString,
	UpdateBuilderTypeToRefsByDataType,
} from '../types/catharsis.types'
import type { NodeRefs } from '../types/refs.types'
import type { CatharsisTraceValue, UpdatePresetString } from '..'
import { defaultToViewItem, fixStructure } from './catharsisUtils'
import { createRepeatedItemOverrideId, getTemplateIdAndItemContext, isRepeatedItemOverrideId } from './repeatersAPI'
import { EMPTY_SCHEMA } from './EMPTY_SCHEMA'
import { updateTwoLevelRecords } from './updateTwoLevelRecords'

const mapNameToQuery = _.invert(queryMapping) as Record<keyof Data, string>
type UpdateDataDependencies = (
	update: Update<void>,
	type: DataMapName,
	id: string,
	dataItem: BaseDataItem | undefined
) => void

export const getCatharsis = ({
	onInvalidation,
	extensions,
	structureStore = new MegaStore(),
	materializerFactory = createMaterializer,
}: CatharsisArgs): Catharsis => {
	let dataStore = new DataStore()
	let originalDataIdToCompId: Record<string, string> = {}
	const { componentNodes = {}, schemas } = extensions
	const dynamicSchemas: RefSchemas = {}

	const getSchemaFromLocalState = (mapName: string, type: string) => {
		const staticSchema = schemas[`${mapName}.json`]?.[type]
		const dynamicSchema = dynamicSchemas[mapName]?.[type]

		return dynamicSchema || staticSchema
	}

	const dataNodes = { ...baseDataNodes, ...extensions.dataNodes }

	const defaultDataToViewItem = defaultToViewItem(getSchemaFromLocalState)

	let traceEnabled = false
	const catharsisTrace = [] as Array<CatharsisTraceValue>

	const updateTrace = (action: string, data: any) => {
		if (traceEnabled) {
			const traceValue = _.last(catharsisTrace)
			traceValue?.actions.push({ action, data })
		}
	}

	const materializer = materializerFactory({
		transform: (value: any, path: Array<string>) => {
			const [namespace, aspect, id] = path
			if (namespace === 'data') {
				const dataItem = dataStore.getData(aspect, id)

				if (typeof dataItem === 'undefined') {
					return undefined
				}

				if (dataNodes[dataItem.type]) {
					return dataNodes[dataItem.type].toViewItem(dataItem, value?.schema, value?.custom)
				}
				return defaultDataToViewItem(aspect, dataItem, value?.schema)
			}

			if (namespace === COMPS_ROOT_NAMESPACE && aspect !== 'modes') {
				const [compId, itemContext] = getTemplateIdAndItemContext(id)
				const contextStore = itemContext ? structureStore.getChildStore(itemContext) : structureStore
				const component = contextStore.getById(compId)
				return component && componentNodes[aspect].toViewItem(component, value)
			}

			return value
		},
	})

	const repeatedSymbol = Symbol('repeated')

	const refs = {
		..._.mapValues(
			componentNodes,
			(__, key) => (component: Component | string) =>
				createCompRef(key, typeof component === 'string' ? component : component?.id) // component?.id is to prevents explosion on data corruption, jira TB-9444
		),
		...compQueriesRefs,
		modes: (modeId: string) => createCompRef('modes', modeId),
		[repeatedSymbol]: false,
	} as NodeRefs<any> & { [repeatedSymbol]: boolean }

	const refsProxy = new Proxy(refs, {
		get: (target, prop, receiver) => {
			if (prop === 'dataQuery' || prop === 'designQuery') {
				refsProxy[repeatedSymbol] = true
			}
			return Reflect.get(target, prop, receiver)
		},
	})

	const getRefsForDataItem = createRefResolver(getSchemaFromLocalState, {})

	const repeatedNodes = _.mapValues(componentNodes, () => new Map<string, boolean>())
	const createTemplateCompNodes = (update: Update<any>, id: string, component?: Component) => {
		for (const compNodeName in componentNodes) {
			if (!component) {
				// delete
				update(COMPS_ROOT_NAMESPACE, compNodeName, id, undefined)
			} else {
				// Reset
				refsProxy[repeatedSymbol] = false
				const dependencies = componentNodes[compNodeName].getDependencies(component, refsProxy) || null
				repeatedNodes[compNodeName].set(id, refsProxy[repeatedSymbol])
				update(COMPS_ROOT_NAMESPACE, compNodeName, id, dependencies, dependencies)
			}
		}

		component?.modes?.definitions?.forEach((modeDefinition) => {
			update(
				COMPS_ROOT_NAMESPACE,
				'modes',
				modeDefinition.modeId,
				{ prefix: modeDefinition.type === 'DEFAULT' ? '[data-mode=default]' : '[data-mode=hover]' },
				EMPTY_SCHEMA
			)
		})
	}
	const createOverridesCompNodes = (update: Update<any>, id: string, context: string, component?: Component) => {
		const compId = createRepeatedItemOverrideId(id, context)
		for (const compNodeName in componentNodes) {
			if (!repeatedNodes[compNodeName].get(id)) {
				continue
			}

			if (!component) {
				// delete
				update(COMPS_ROOT_NAMESPACE, compNodeName, compId, undefined)
			} else {
				const dependencies = componentNodes[compNodeName].getDependencies(component, refs) || null
				update(COMPS_ROOT_NAMESPACE, compNodeName, compId, dependencies, dependencies)
			}
		}
	}

	const repeatedComponentOverrides = new Map<string, Partial<Component>>()

	const updateDataDependencies: UpdateDataDependencies = (update, type, id, dataItem) => {
		if (!dataItem) {
			if (isRepeatedItemOverrideId(id)) {
				repeatedComponentOverrides.set(id, { [mapNameToQuery[type]]: undefined })
			}
			update('data', type, id, undefined)
			return
		}
		const schemaRefs = getRefsForDataItem(dataItem, type)
		const customDeps = dataNodes[dataItem.type]?.getDependencies(dataItem)
		const dependencies = schemaRefs || customDeps ? { schema: schemaRefs, custom: customDeps } : null

		update('data', type, id, dependencies, dependencies)

		if (isRepeatedItemOverrideId(id)) {
			const overrides = { [mapNameToQuery[type]]: id }
			repeatedComponentOverrides.set(id, overrides)
		}
	}

	const updateData: Batched<UpdateData> = (update) => (type, id, dataItem) => {
		updateTrace('updateData', { type, id, dataItem })
		updateDataDependencies(update, type, id, dataStore.updateData(type, id, dataItem))
	}

	const updateSvgString: Batched<UpdateSvgString> = (update) => (id, svgString) => {
		updateTrace('updateSvgString', { id, svgString })
		update(SVG_STORE_NAMESPACE, 'parsedSvg', id, parseSvgString(svgString, id), EMPTY_SCHEMA)
	}

	const updatePresetCss: Batched<UpdatePresetString> = (update) => (widgetId, presetIdToCssString) => {
		update('presetsCss', 'widgets', widgetId, presetIdToCssString, EMPTY_SCHEMA)
	}
	const updatePathToSelectorMap: Batched<UpdatePathToSelectorMap> = (update) => (compId, pathToSelector) => {
		update('selectorStore', 'pathToSelector', compId, pathToSelector, EMPTY_SCHEMA)
	}
	const updateBuilderTypeToReducedManifest: Batched<UpdateBuilderTypeToReducedManifest> = (update) => (builderType, reducedManifest) => {
		update('environment', 'builderTypeToReducedManifest', builderType, reducedManifest, EMPTY_SCHEMA)
	}
	const updateBuilderTypeToPresetInfo: Batched<UpdateBuilderTypeToPresetInfo> = (update) => (builderType, builderTypeToPresetInfo) => {
		update('environment', 'builderTypeToPresetInfo', builderType, builderTypeToPresetInfo, EMPTY_SCHEMA)
	}
	const updateBuilderTypeToRefsByDataType: Batched<UpdateBuilderTypeToRefsByDataType> = (update) => (builderType, dataTypeToRefs) => {
		update('environment', 'builderTypeToRefsByDataType', builderType, dataTypeToRefs, EMPTY_SCHEMA)
	}

	const updateDataItemIdToCompId = (component: Component | undefined) => {
		if (!component) {
			return
		}
		const { dataQuery, designQuery } = component
		if (dataQuery) {
			originalDataIdToCompId[dataQuery] = component.id
		}
		if (designQuery) {
			originalDataIdToCompId[designQuery] = component.id
		}
	}

	const updateStructure: Batched<UpdateStructure> = (update) => (id, component) => {
		updateTrace('updateStructure', { id, component })
		if (!component) {
			structureStore.removeById(id)
			createTemplateCompNodes(update, id)
			update('QA', 'fullNameCompType', id, undefined)
		} else {
			const fixedComponent = fixStructure(component)
			structureStore.updateById(id, fixedComponent)
			updateDataItemIdToCompId(fixedComponent)
			createTemplateCompNodes(update, id, fixedComponent)
			update('QA', 'fullNameCompType', id, component.componentType, EMPTY_SCHEMA)
		}
	}

	const updateEnvironment: Batched<UpdateEnvironment> = (update) => (environment) => {
		updateTrace('updateEnvironment', { environment })
		const {
			renderFlags,
			compIdToIgnoredTransformProps,
			ignoredTransformPropsForType,
			ooiCssConfigStore,
			clientSpecMap,
			...atoms
		} = environment
		for (const renderFlag in renderFlags) {
			update('environment', 'renderFlags', renderFlag, renderFlags[renderFlag], EMPTY_SCHEMA)
		}

		if (clientSpecMap) {
			const specMapByAppDefId = _(clientSpecMap).filter('appDefinitionId').keyBy('appDefinitionId').value()
			update('environment', 'atoms', 'clientSpecMap', specMapByAppDefId, EMPTY_SCHEMA)
		}

		for (const key in ooiCssConfigStore) {
			update('environment', 'ooiCssConfigStore', key, ooiCssConfigStore[key], EMPTY_SCHEMA)
		}

		for (const compId in compIdToIgnoredTransformProps) {
			update(
				'environment',
				'compIdToIgnoredTransformProps',
				compId,
				compIdToIgnoredTransformProps[compId],
				EMPTY_SCHEMA
			)
		}
		if (ignoredTransformPropsForType) {
			updateTwoLevelRecords(materializer)(
				update,
				['environment', 'ignoredTransformPropsForType'],
				ignoredTransformPropsForType
			)
		}
		for (const atom in atoms) {
			// @ts-expect-error
			update('environment', 'atoms', atom, atoms[atom], EMPTY_SCHEMA)
		}

		if (atoms.viewMode) {
			update('environment', 'mobile', 'isMobileView', atoms.viewMode === 'mobile', EMPTY_SCHEMA)
			const isNonAdaptiveMobile =
				!environment.isResponsive &&
				environment.deviceType === 'Smartphone' &&
				environment.viewMode === 'desktop'
			update('environment', 'mobile', 'isNonAdaptiveMobile', isNonAdaptiveMobile, EMPTY_SCHEMA)
			update('environment', 'mobile', 'deviceType', environment.deviceType, EMPTY_SCHEMA)
		}
	}

	const transaction: Catharsis['transaction'] = (update, runAndReport = (fn) => fn()) => {
		const traceValue = traceEnabled && ({ actions: [], invalidations: [] } as any)
		if (traceValue) {
			catharsisTrace.push(traceValue)
		}
		const invalidations = runAndReport(
			() =>
				materializer.batch((batchUpdate) => {
					runAndReport(
						() =>
							update({
								updateData: updateData(batchUpdate),
								updateStructure: updateStructure(batchUpdate),
								updateEnvironment: updateEnvironment(batchUpdate),
								updateSvgString: updateSvgString(batchUpdate),
								updatePresetCss: updatePresetCss(batchUpdate),
								updatePathToSelectorMap: updatePathToSelectorMap(batchUpdate),
								updateBuilderTypeToReducedManifest: updateBuilderTypeToReducedManifest(batchUpdate),
								updateBuilderTypeToPresetInfo: updateBuilderTypeToPresetInfo(batchUpdate),
								updateBuilderTypeToRefsByDataType: updateBuilderTypeToRefsByDataType(batchUpdate),
							}),
						'materializer.update'
					)

					// Add virtual structure for the overrides data items
					if (repeatedComponentOverrides.size) {
						runAndReport(
							() =>
								repeatedComponentOverrides.forEach((overrides, id) => {
									const [dataId, context] = getTemplateIdAndItemContext(id)
									const compId = originalDataIdToCompId[dataId]
									const contextStore = structureStore.getChildStore(context)
									contextStore.updateById(compId, overrides)
									const component = contextStore.getById(compId)
									createOverridesCompNodes(batchUpdate, compId, context, component)
								}),
							'repeatedComponentOverrides.forEach'
						)
						runAndReport(() => repeatedComponentOverrides.clear(), 'repeatedComponentOverrides.clear()')
					}
				}, runAndReport),
			'materializer.batch'
		) as Array<Array<string>>

		runAndReport(() => {
			for (const invalidation of invalidations) {
				if (traceValue) {
					traceValue?.invalidations.push({ path: invalidation, value: materializer.get(invalidation) })
				}
				onInvalidation(invalidation, materializer.get)
			}
		}, 'onInvalidation')
	}

	const trace = {
		start: () => {
			if (!traceEnabled) {
				traceEnabled = true
				catharsisTrace.length = 0
			}
		},
		end: () => {
			traceEnabled = false
		},
		clear: () => {
			catharsisTrace.length = 0
		},
		get: () => catharsisTrace,
	}

	const addDynamicSchemas = (addedSchemas: RefSchemas) => {
		for (const mapName in addedSchemas) {
			if (!dynamicSchemas[mapName]) {
				dynamicSchemas[mapName] = {}
			}
			Object.assign(dynamicSchemas[mapName], addedSchemas[mapName])
		}
	}

	function cleanAll() {
		structureStore?.cleanAll()
		materializer.cleanAll()
		dataStore = new DataStore()
		originalDataIdToCompId = {}
	}

	return {
		transaction,
		addDynamicSchemas,
		trace,
		cleanAll,
	}
}
